import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, takeUntil } from 'rxjs';
import { UserRegister } from '../../models/user-register.model';
import { AuthService } from '../../services/auth.service';
import { EmailVerifyModalService } from '../../services/email-verify-modal.service';
import { selectIsArabic } from '../../store/selectors/language.selectors';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  standalone: false,
})
export class RegisterComponent implements OnInit, OnDestroy {
  registerForm!: FormGroup;
  isSubmitted = false;
  isLoading = false;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;

  modalInstance: any;

  isArabic$: Observable<boolean> = this.store.select(selectIsArabic);
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private router: Router,
    private store: Store,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private modalService: EmailVerifyModalService,
    private traslate: TranslateService
  ) {}

  ngOnInit(): void {
    this.registerForm = this.fb.group(
      {
        email: ['', [Validators.required, Validators.email]],
        name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(25)]],
        phoneNumber: ['', [Validators.required]],
        password: [
          '',
          [
            Validators.required,
            Validators.pattern(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/),
          ],
        ],
        confirmPassword: [
          '',
          [
            Validators.required,
            Validators.pattern(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/),
          ],
        ],
        termsAccepted: [false, Validators.required],
      },
      {
        validators: this.passwordsMatchValidator,
      }
    );
  }

  get formControls() {
    return this.registerForm.controls;
  }

  passwordsMatchValidator(form: FormGroup) {
    const password = form.get('password')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordsMismatch: true };
  }

  onSubmit() {
    this.isSubmitted = true;

    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      this.toastr.error(
        this.traslate.instant('LOGIN_FORM_ERROR'),
        this.traslate.instant('VALIDATION_ERROR')
      );
      return;
    }

    this.isLoading = true;

    this.authService
      .register(this.registerForm.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.router.navigate(['/']);
          this.isLoading = false;

          this.openModal();
        },
        error: (err) => {
          this.toastr.error(
            this.traslate.instant(err?.error?.detail === 'TERMS_UNAVAILABLE' || err?.error?.detail === 'TERMS_REQUIRED' ? err.error.detail : 'REGISTER_FAIL_ERROR'),
            this.traslate.instant('ERROR')
          );
          console.error(err);
          this.isLoading = false;
        },
      });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  openModal() {
    this.modalService.open((this.registerForm.getRawValue() as UserRegister).email);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get passwordValue(): string {
    return this.registerForm.get('password')?.value || '';
  }

  get hasMinLength(): boolean {
    return this.passwordValue.length >= 8;
  }

  get hasUpperCase(): boolean {
    return /[A-Z]/.test(this.passwordValue);
  }

  get hasNumber(): boolean {
    return /\d/.test(this.passwordValue);
  }

  get hasSpecialChar(): boolean {
    return /[!@#$%^&*]/.test(this.passwordValue);
  }

  get email() {
    return this.registerForm.get('email');
  }
  get name() {
    return this.registerForm.get('name');
  }
  get phoneNumber() {
    return this.registerForm.get('phoneNumber');
  }
  get password() {
    return this.registerForm.get('password');
  }
  get confirmPassword() {
    return this.registerForm.get('confirmPassword');
  }
  get termsAccepted() {
    return this.registerForm.get('termsAccepted');
  }
}
